import { fetchAuthSession } from 'aws-amplify/auth';

class TokenRefreshManager {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.lastRefreshTime = 0;
    this.refreshDebounceTime = 10000; // 10 seconds minimum between refreshes
  }

  /**
   * Parse JWT without verification to get expiration time
   * Security: Only extracts exp claim for scheduling, not for authorization
   */
  parseToken(token) {
    try {
      // Validate token format first
      if (typeof token !== 'string' || !token.includes('.')) {
        return null;
      }
      
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      
      // Safely decode base64 payload
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      
      // Only return exp claim to minimize exposure
      return { exp: payload.exp, iss: payload.iss };
    } catch (error) {
      // Don't log token details in error
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to parse token');
      }
      return null;
    }
  }

  /**
   * Calculate time until token expires (in milliseconds)
   */
  getTimeUntilExpiry(token) {
    const tokenData = this.parseToken(token);
    if (!tokenData || !tokenData.exp) return 0;
    
    const expiryTime = tokenData.exp * 1000; // Convert to milliseconds
    const now = Date.now();
    return Math.max(0, expiryTime - now); // Never return negative
  }

  /**
   * Start monitoring token and refresh before expiry
   */
  startTokenRefreshTimer() {
    // Clear any existing timer
    this.stopTokenRefreshTimer();
    
    // Get current token
    const token = sessionStorage.getItem('complexappToken') || localStorage.getItem('complexappToken');
    if (!token) {
      console.log('No token to monitor');
      return;
    }
    
    // Check if it's a Cognito token
    const tokenData = this.parseToken(token);
    const isCognitoToken = tokenData?.iss?.includes('cognito-idp');
    
    if (!isCognitoToken) {
      console.log('Not a Cognito token, skipping proactive refresh');
      return;
    }
    
    // Calculate refresh time (15 minutes before expiry for 60-minute tokens)
    const timeUntilExpiry = this.getTimeUntilExpiry(token);
    const refreshBuffer = 15 * 60 * 1000; // 15 minutes in milliseconds
    const refreshIn = Math.max(0, timeUntilExpiry - refreshBuffer);
    
    if (refreshIn <= 0) {
      console.log('Token expired or expiring soon, refreshing immediately');
      this.refreshToken();
      return;
    }
    
    // Log scheduling only in development
    if (process.env.NODE_ENV === 'development') {
      const minutes = Math.floor(refreshIn / 60000);
      const seconds = Math.floor((refreshIn % 60000) / 1000);
      console.log(`⏰ Token refresh scheduled in ${minutes}m ${seconds}s`);
    }
    
    // Set timer for refresh
    this.refreshTimer = setTimeout(() => {
      if (process.env.NODE_ENV === 'development') {
        console.log('⏰ Proactive token refresh triggered');
      }
      this.refreshToken();
    }, refreshIn);
  }

  /**
   * Stop the refresh timer
   */
  stopTokenRefreshTimer() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Refresh the Cognito token
   */
  async refreshToken() {
    // Debounce: Prevent refresh if one happened recently
    const now = Date.now();
    const timeSinceLastRefresh = now - this.lastRefreshTime;

    if (timeSinceLastRefresh < this.refreshDebounceTime) {
      console.log(`⏳ Skipping refresh, last refresh was ${Math.floor(timeSinceLastRefresh / 1000)}s ago`);
      return;
    }

    if (this.isRefreshing) {
      console.log('Already refreshing token, skipping duplicate request');
      return;
    }

    this.isRefreshing = true;
    this.lastRefreshTime = now;
    
    try {
      console.log('🔄 Proactively refreshing Cognito token...');
      
      // Use Amplify to refresh the session
      const session = await fetchAuthSession({ forceRefresh: true });
      
      if (session.tokens?.idToken) {
        const newToken = session.tokens.idToken.toString();
        
        // Update storage
        sessionStorage.setItem('complexappToken', newToken);
        localStorage.setItem('complexappToken', newToken);
        
        console.log('✅ Token refreshed proactively');

        // Update last refresh time
        this.lastRefreshTime = Date.now();

        // Schedule next refresh (avoid recursion during visibility-triggered refreshes)
        if (!this.skipNextSchedule) {
          this.startTokenRefreshTimer();
        }
        this.skipNextSchedule = false;
        
        // Dispatch event for other components to know token was refreshed
        window.dispatchEvent(new CustomEvent('tokenRefreshed', { 
          detail: { token: newToken } 
        }));
        
        return newToken;
      } else {
        console.error('❌ No ID token in refreshed session');
      }
    } catch (error) {
      console.error('❌ Proactive token refresh failed:', error);
      
      // If refresh fails, try to re-authenticate
      if (error.message?.includes('expired')) {
        console.log('Session expired, user needs to re-authenticate');
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Initialize token monitoring when user logs in
   */
  initialize() {
    console.log('🚀 Initializing token refresh manager');
    
    // Start monitoring current token
    this.startTokenRefreshTimer();
    
    // Listen for storage changes (in case token is updated elsewhere)
    window.addEventListener('storage', (e) => {
      if (e.key === 'complexappToken') {
        console.log('Token changed in storage, restarting timer');
        this.startTokenRefreshTimer();
      }
    });
    
    // Listen for visibility changes to refresh when tab becomes active
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        const token = sessionStorage.getItem('complexappToken') || localStorage.getItem('complexappToken');
        if (token) {
          const timeUntilExpiry = this.getTimeUntilExpiry(token);

          // If token expires in less than 20 minutes, refresh it (with debounce)
          if (timeUntilExpiry < 20 * 60 * 1000 && timeUntilExpiry > 0) {
            const timeSinceLastRefresh = Date.now() - this.lastRefreshTime;
            if (timeSinceLastRefresh >= this.refreshDebounceTime) {
              console.log('Tab became visible, token expiring soon, refreshing...');
              this.skipNextSchedule = true; // Prevent scheduling recursion
              this.refreshToken();
            } else {
              console.log(`Tab visible but refresh debounced (${Math.floor(timeSinceLastRefresh / 1000)}s since last)`);
            }
          }
        }
      }
    });
  }

  /**
   * Clean up when user logs out
   */
  cleanup() {
    console.log('🧹 Cleaning up token refresh manager');
    this.stopTokenRefreshTimer();
  }
}

// Create singleton instance
const tokenRefreshManager = new TokenRefreshManager();

export default tokenRefreshManager;